import { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/core/components/ui/dialog';
import { Button } from '@/core/components/ui/button';
import { Label } from '@/core/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import { Progress } from '@/core/components/ui/progress';
import { Upload, X, AlertCircle, Music, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { ApplicationType, MediaType } from '../../types/translation-types';
import {
  UploadSoundRequest,
  isValidAudioFile,
  isValidFileSize,
  formatFileSize,
  MAX_AUDIO_FILE_SIZE,
  SUPPORTED_AUDIO_EXTENSIONS,
} from '../../types/sound-management-types';

interface FileWithStatus {
  id: string;
  file: File;
  languageCode: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

// Helper function to recursively extract all files from a directory entry
async function getAllFilesFromEntry(entry: FileSystemEntry): Promise<File[]> {
  const files: File[] = [];

  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntry;
    return new Promise((resolve) => {
      fileEntry.file((file) => {
        resolve([file]);
      }, () => resolve([]));
    });
  } else if (entry.isDirectory) {
    const dirEntry = entry as FileSystemDirectoryEntry;
    const dirReader = dirEntry.createReader();

    return new Promise((resolve) => {
      const readEntries = () => {
        dirReader.readEntries(async (entries) => {
          if (entries.length === 0) {
            resolve(files);
          } else {
            // Process all entries in this directory
            for (const entry of entries) {
              const entryFiles = await getAllFilesFromEntry(entry);
              files.push(...entryFiles);
            }
            // Read more entries (directories may return results in batches)
            readEntries();
          }
        }, () => resolve(files));
      };
      readEntries();
    });
  }

  return files;
}

interface UploadSoundDialogProps {
  open: boolean;
  onClose: () => void;
  applicationType: ApplicationType;
  availableLanguages: { languageCode: string; languageName: string }[];
  onUpload: (request: UploadSoundRequest, skipRefresh?: boolean) => Promise<void>;
  onRefresh: () => void;
  isUploading: boolean;
  error: string | null;
}

// Try to extract language code from filename (e.g., "en_authorizing.mp3" -> "en")
// Supports language variants like en_gb, en_au even if not in configured languages list
// Detects language codes anywhere in the filename
// Handles both underscore (en_gb) and hyphen (en-gb) formats
function extractLanguageFromFilename(filename: string, availableLanguages: string[]): string | null {
  const namePart = filename.split('.')[0].toLowerCase();

  // Sort by length descending to check more specific language codes first (e.g., en-gb before en)
  const sortedLanguages = [...availableLanguages].sort((a, b) => b.length - a.length);

  // First pass: Check for exact matches with configured languages (including variants like en-gb)
  // This prevents false positives like "en_scan" being detected as "en_sc" variant
  for (const langCode of sortedLanguages) {
    const lowerLangCode = langCode.toLowerCase();
    // Create underscore variant for matching against filename (en-gb -> en_gb)
    const underscoreVariant = lowerLangCode.replace('-', '_');

    // Check exact match (entire filename is just the language code)
    if (namePart === underscoreVariant) {
      return langCode;
    }

    // Check at start: en_sound.mp3 or en_gb_sound.mp3
    if (namePart.startsWith(underscoreVariant + '_')) {
      return langCode;
    }

    // Check at end: sound_en.mp3 or sound_en_gb.mp3
    if (namePart.endsWith('_' + underscoreVariant)) {
      return langCode;
    }

    // Check in middle: sound_en_file.mp3 or sound_en_gb_file.mp3
    if (namePart.includes('_' + underscoreVariant + '_')) {
      return langCode;
    }
  }

  return null;
}

export function UploadSoundDialog({
  open,
  onClose,
  applicationType,
  availableLanguages,
  onUpload,
  onRefresh,
}: UploadSoundDialogProps) {
  const [files, setFiles] = useState<FileWithStatus[]>([]);
  const [defaultLanguage, setDefaultLanguage] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessingFolder, setIsProcessingFolder] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const availableLangCodes = availableLanguages.map(l => l.languageCode);

  const resetForm = useCallback(() => {
    setFiles([]);
    setDefaultLanguage('');
    setIsDragging(false);
    setIsUploading(false);
    setUploadProgress(0);
    setIsProcessingFolder(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleClose = useCallback(() => {
    if (!isUploading && !isProcessingFolder) {
      resetForm();
      onClose();
    }
  }, [isUploading, isProcessingFolder, resetForm, onClose]);

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles);
    const validFiles: FileWithStatus[] = [];

    for (const file of fileArray) {
      if (!isValidAudioFile(file)) {
        continue; // Skip invalid files
      }
      if (!isValidFileSize(file)) {
        continue; // Skip oversized files
      }

      // Try to detect language from filename
      const detectedLang = extractLanguageFromFilename(file.name, availableLangCodes);

      validFiles.push({
        id: `${file.name}-${Date.now()}-${Math.random()}`,
        file,
        languageCode: detectedLang || defaultLanguage || '',
        status: 'pending',
      });
    }

    setFiles(prev => [...prev, ...validFiles]);
  }, [availableLangCodes, defaultLanguage]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        addFiles(e.target.files);
      }
    },
    [addFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const items = e.dataTransfer.items;
      if (!items || items.length === 0) {
        // Fallback to files if items API not available
        if (e.dataTransfer.files) {
          addFiles(e.dataTransfer.files);
        }
        return;
      }

      setIsProcessingFolder(true);
      const allFiles: File[] = [];

      try {
        // Process all dropped items (files and folders)
        for (let i = 0; i < items.length; i++) {
          const item = items[i];

          if (item.kind === 'file') {
            const entry = item.webkitGetAsEntry?.();
            if (entry) {
              const files = await getAllFilesFromEntry(entry);
              allFiles.push(...files);
            } else {
              // Fallback for browsers that don't support webkitGetAsEntry
              const file = item.getAsFile();
              if (file) {
                allFiles.push(file);
              }
            }
          }
        }

        if (allFiles.length > 0) {
          addFiles(allFiles);
        }
      } catch (error) {
        console.error('Error processing dropped items:', error);
      } finally {
        setIsProcessingFolder(false);
      }
    },
    [addFiles]
  );

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemoveFile = useCallback((id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  }, []);

  const handleFileLanguageChange = useCallback((id: string, languageCode: string) => {
    setFiles(prev => prev.map(f =>
      f.id === id ? { ...f, languageCode } : f
    ));
  }, []);

  const handleDefaultLanguageChange = useCallback((lang: string) => {
    const actualLang = lang === '_none_' ? '' : lang;
    setDefaultLanguage(actualLang);
    // Update all files without a language to use the new default
    if (actualLang) {
      setFiles(prev => prev.map(f =>
        f.languageCode === '' ? { ...f, languageCode: actualLang } : f
      ));
    }
  }, []);

  const handleUpload = useCallback(async () => {
    const filesToUpload = files.filter(f => f.status === 'pending' && f.languageCode);
    if (filesToUpload.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);

    // For batch uploads, skip refresh for each file
    const isBatchUpload = filesToUpload.length > 1;

    let completed = 0;
    for (const fileItem of filesToUpload) {
      // Mark as uploading
      setFiles(prev => prev.map(f =>
        f.id === fileItem.id ? { ...f, status: 'uploading' } : f
      ));

      try {
        await onUpload({
          file: fileItem.file,
          applicationType,
          mediaType: MediaType.Sound,
          languageCode: fileItem.languageCode,
        }, isBatchUpload); // Skip refresh for batch uploads

        // Mark as success
        setFiles(prev => prev.map(f =>
          f.id === fileItem.id ? { ...f, status: 'success' } : f
        ));
      } catch (err) {
        // Mark as error
        setFiles(prev => prev.map(f =>
          f.id === fileItem.id ? {
            ...f,
            status: 'error',
            error: err instanceof Error ? err.message : 'Upload failed'
          } : f
        ));
      }

      completed++;
      setUploadProgress(Math.round((completed / filesToUpload.length) * 100));
    }

    // Refresh once after all uploads complete (for batch uploads)
    if (isBatchUpload) {
      onRefresh();
    }

    setIsUploading(false);
  }, [files, applicationType, onUpload, onRefresh]);

  const pendingFiles = files.filter(f => f.status === 'pending');
  const canUpload = pendingFiles.length > 0 && pendingFiles.every(f => f.languageCode);
  const hasCompletedFiles = files.some(f => f.status === 'success' || f.status === 'error');
  const allComplete = files.length > 0 && files.every(f => f.status === 'success' || f.status === 'error');

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Upload Sound Files</DialogTitle>
          <DialogDescription>
            Upload audio files for the translation system. You can upload multiple files at once.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {/* Default Language Selection */}
          <div className="space-y-2">
            <Label htmlFor="defaultLanguage">Default Language for New Files</Label>
            <Select value={defaultLanguage || '_none_'} onValueChange={handleDefaultLanguageChange}>
              <SelectTrigger id="defaultLanguage">
                <SelectValue placeholder="Select default language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none_">Auto-detect from filename</SelectItem>
                {availableLanguages.map((lang) => (
                  <SelectItem key={lang.languageCode} value={lang.languageCode}>
                    {lang.languageName} ({lang.languageCode})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Files named like &quot;en_sound.mp3&quot; or &quot;de_sound.mp3&quot; will auto-detect the language.
            </p>
          </div>

          {/* File Drop Zone */}
          <div className="space-y-2">
            <Label>Audio Files</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp3,.wav,.ogg,audio/mpeg,audio/wav,audio/ogg"
              onChange={handleFileChange}
              className="hidden"
              multiple
            />
            <div
              onClick={isProcessingFolder ? undefined : handleBrowseClick}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={cn(
                'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
                isProcessingFolder
                  ? 'border-gray-300 bg-gray-50 cursor-wait'
                  : isDragging
                  ? 'border-primary bg-primary/5 cursor-pointer'
                  : 'border-gray-300 hover:border-gray-400 cursor-pointer'
              )}
            >
              {isProcessingFolder ? (
                <>
                  <Loader2 className="mx-auto h-8 w-8 text-primary animate-spin" />
                  <p className="mt-2 text-sm text-gray-600">Processing folder...</p>
                </>
              ) : (
                <>
                  <Upload className="mx-auto h-8 w-8 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-600">
                    Drop audio files or folders here or{' '}
                    <span className="text-primary font-medium">browse</span>
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Supported: {SUPPORTED_AUDIO_EXTENSIONS.join(', ')} | Max: {formatFileSize(MAX_AUDIO_FILE_SIZE)} per file
                  </p>
                </>
              )}
            </div>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="space-y-2">
              <Label>Files to Upload ({files.length})</Label>
              <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
                {files.map((fileItem) => (
                  <div key={fileItem.id} className="p-3 flex items-center gap-3">
                    {/* Status Icon */}
                    <div className="flex-shrink-0">
                      {fileItem.status === 'pending' && (
                        <Music className="h-5 w-5 text-gray-400" />
                      )}
                      {fileItem.status === 'uploading' && (
                        <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                      )}
                      {fileItem.status === 'success' && (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      )}
                      {fileItem.status === 'error' && (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                    </div>

                    {/* File Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{fileItem.file.name}</p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(fileItem.file.size)}
                        {fileItem.error && (
                          <span className="text-red-500 ml-2">{fileItem.error}</span>
                        )}
                      </p>
                    </div>

                    {/* Language Selector */}
                    {fileItem.status === 'pending' && (
                      <Select
                        value={fileItem.languageCode || '_none_'}
                        onValueChange={(val) => handleFileLanguageChange(fileItem.id, val === '_none_' ? '' : val)}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue placeholder="Language" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none_" disabled>Select...</SelectItem>
                          {availableLanguages.map((lang) => (
                            <SelectItem key={lang.languageCode} value={lang.languageCode}>
                              {lang.languageCode.toUpperCase()}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {/* Language Badge for completed files */}
                    {fileItem.status !== 'pending' && (
                      <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                        {fileItem.languageCode.toUpperCase()}
                      </span>
                    )}

                    {/* Remove Button */}
                    {fileItem.status === 'pending' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFile(fileItem.id)}
                        className="h-8 w-8 p-0 flex-shrink-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Missing Language Warning */}
          {pendingFiles.some(f => !f.languageCode) && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Some files don&apos;t have a language selected. Please select a language for each file.
              </AlertDescription>
            </Alert>
          )}

          {/* Upload Progress */}
          {isUploading && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isUploading || isProcessingFolder}>
            {allComplete ? 'Close' : 'Cancel'}
          </Button>
          {!allComplete && (
            <Button onClick={handleUpload} disabled={!canUpload || isUploading || isProcessingFolder}>
              {isUploading ? 'Uploading...' : isProcessingFolder ? 'Processing...' : `Upload ${pendingFiles.length} File${pendingFiles.length !== 1 ? 's' : ''}`}
            </Button>
          )}
          {hasCompletedFiles && !isUploading && pendingFiles.length > 0 && (
            <Button onClick={handleUpload} disabled={!canUpload || isProcessingFolder}>
              Upload Remaining
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
